import React, { useEffect, useMemo, useState } from 'react'

import { faTimes } from '@fortawesome/pro-light-svg-icons/faTimes'

import { AnalyticsQueryDatapointEnum, AnalyticsQueryQueryEnum, Product } from '@infura/packages.api'
import { ActionLink } from '@infura/packages.ui/ActionLink'
import { Heading } from '@infura/packages.ui/Heading'
import { Tooltip } from '@infura/packages.ui/Tooltip'

import TourKeysAndRequests from 'components/gettingStartedTour/TourKeysAndRequests'
import { LoadingSkeleton } from 'components/gettingStartedTour/TourSkeleon'
import TourStepper from 'components/gettingStartedTour/TourStepper'
import { steps } from 'components/gettingStartedTour/data'
import { IStepsComponent } from 'components/gettingStartedTour/types'

import { useDynamicStats1_5_Query } from 'hooks/useStats'
import { useUKSProjects } from 'hooks/useUKSProjects'
import useVisibilityCookie, { GS_VISIBILITY } from 'hooks/useVisibilityCookie'

const TourContainer = () => {
  // if stats last 30 days no requests
  const [isGSTilesVisible, toggleGSTilesVisible] = useVisibilityCookie(GS_VISIBILITY)
  const { isLoading: isUserKeysLoading, mutate: revalidateUksKeys, projects: userKeys = {} } = useUKSProjects()
  const [step, setStep] = useState(3)
  const [isFullsize, setIsFullsize] = useState(false)
  const [networkSelected, setNetworkSelected] = useState(null)
  const [apiKeySelected, setApiKeySelected] = useState(null)
  const [subnetUrl, setSubnetUrl] = useState('')
  const apiKeysNumber = useMemo(() => Object.values(userKeys).length, [userKeys])

  const { data, isLoading: isStatsLoading } = useDynamicStats1_5_Query({
    datapoint: AnalyticsQueryDatapointEnum.COUNT,
    product: Product.ETHEREUM,
    projectIds: (Object as any).values(userKeys).map(key => key.id),
    query: AnalyticsQueryQueryEnum.TIMESERIES,
    time: '30d'
  })

  useEffect(() => {
    if (apiKeysNumber === 0) {
      // @ts-expect-error enabled to support strictNullChecks - Please fix this if you can!
      setApiKeySelected('')
    } else if (apiKeysNumber === 1) {
      setApiKeySelected((Object as any).values(userKeys)?.[0]?.id)
    }
  }, [userKeys, apiKeysNumber, setApiKeySelected, isUserKeysLoading])

  useEffect(() => {
    if (!isStatsLoading && data?.data?.[0]?.result?.length === 0) {
      setStep(1)
      setIsFullsize(false)
    } else {
      setIsFullsize(true)
      setStep(3)
    }
  }, [data, setStep, isStatsLoading])

  const handleNextStep = () => setStep(step => step + 1)
  const handlePrevStep = () => setStep(step => step - 1)

  const StepComponent: React.FC<IStepsComponent> = steps[step].component

  const renderStepComponent = () => (
    <StepComponent
      apiKeySelected={apiKeySelected}
      handleNextStep={handleNextStep}
      handlePrevStep={handlePrevStep}
      // @ts-expect-error enabled to support strictNullChecks - Please fix this if you can!
      networkSelected={networkSelected}
      // @ts-expect-error enabled to support strictNullChecks - Please fix this if you can!
      revalidateUksKeys={revalidateUksKeys}
      // @ts-expect-error enabled to support strictNullChecks - Please fix this if you can!
      setApiKeySelected={setApiKeySelected}
      // @ts-expect-error enabled to support strictNullChecks - Please fix this if you can!
      setNetworkSelected={setNetworkSelected}
      setSubnetUrl={setSubnetUrl}
      subnetUrl={subnetUrl}
      userKeys={userKeys}
    />
  )

  return (
    isGSTilesVisible && (
      <div className='mx-auto'>
        <div className='mb-4 flex items-center justify-between'>
          <div className='pr-4'>
            <Heading theme='page'>Let's Get Started</Heading>
          </div>
          <Tooltip
            content='Don’t worry, you can come back to this anytime from your settings'
            popperOptions={{ placement: 'left' }}
          >
            <ActionLink
              as='div'
              icon={faTimes}
              iconPosition='left'
              id='close-getting-started'
              onClick={() => toggleGSTilesVisible(false)}
            >
              Hide
            </ActionLink>
          </Tooltip>
        </div>
        <div className='md:flex'>
          {isUserKeysLoading || isStatsLoading ? (
            <LoadingSkeleton />
          ) : isFullsize ? (
            <TourKeysAndRequests className='border-border-muted bg-background-default mb-6 h-full w-full rounded-md border p-8'>
              {renderStepComponent()}
            </TourKeysAndRequests>
          ) : (
            <>
              <div className='mb-6 md:flex-none md:pr-6'>
                <div className='border-border-muted bg-background-default w-full rounded-md border p-8 md:h-full md:w-[320px]'>
                  <TourStepper currentStep={step} steps={steps} />
                </div>
              </div>
              <div className='mb-6 md:w-[calc(100%-340px)] md:grow'>
                <TourKeysAndRequests className='border-border-muted bg-background-default h-full rounded-md border p-8'>
                  {renderStepComponent()}
                </TourKeysAndRequests>
              </div>
            </>
          )}
        </div>
      </div>
    )
  )
}

export default TourContainer
